import { observer } from 'mobx-react'
import style from './HomePage.module.less'
import { useTranslationWithType } from '@/i18n/i18n'
import Layout from '@/components/Layout/Layout'
import classNames from 'classnames'
import { CSSProperties, forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import AmeIcon from '@/components/AmeIcon/AmeIcon'
import { commonUtils } from '@/helpers/commonUtils'
import { userService } from '@/services/userService'
import { uiController } from '@/controllers/uiController'
import { vipService } from '@/services/vipService'
import { ProductItemType } from '@/components/PageFooter'

type AiAssistantFunc = {
  setTab: (to: string) => void
}

const doStart = () => {
  userService.goToInstall()
}
const HomePage = observer(() => {
  const ref = useRef<HTMLDivElement>(null)
  const scrollTo = (el: HTMLDivElement) => {
    // 顶栏的高度
    const headerHeight = 68

    const elTop = el.getBoundingClientRect().top
    const top = elTop + ref.current!.scrollTop
    ref.current?.scrollTo({ top: top - headerHeight, behavior: 'smooth' })
  }

  const AiAssistantRef = useRef<AiAssistantFunc>(null)

  useEffect(() => {
    const { from } = commonUtils.getUrlParams()
    if (from && from === 'login_succ') {
      // 如果是登录成功跳转到该页面，则需要校验是否要弹支付弹窗 & 向插件发送事件
      commonUtils.sendExtensionMessage('markAuth', {})
    }
  }, [])
  useEffect(() => {
    if (!userService.isLogin) {
      return
    }
    uiController.checkTrialPlan()
  }, [userService.isLogin])

  const footerProductItemClick = (item: ProductItemType) => {
    if (item === 'pdf') {
      const el = document.getElementById('pdfDiv') as HTMLDivElement
      el && scrollTo(el)
    } else {
      const el = document.getElementById('translateDiv') as HTMLDivElement
      el && scrollTo(el)
      AiAssistantRef.current?.setTab(item)
    }
  }

  return (
    <Layout
      rootClass={style['home-page']}
      ref={ref}
      onFooterProductItemClick={(val) => footerProductItemClick(val)}
    >
      <div className={classNames(style.bgBox)}>
        <img
          className={style['home-page-bg1']}
          src={commonUtils.getStaticFile('home/top-bg-1.png')}
          alt=''
        />
        <img
          className={style['home-page-bg2']}
          src={commonUtils.getStaticFile('home/top-bg-2.png')}
          alt=''
        />
        <img
          className={style['home-page-bg3']}
          src={commonUtils.getStaticFile('home/top-bg-3.png')}
          alt=''
        />
      </div>
      <div className={style['home-page-content']}>
        <Top />
        <HowItWork />
        <PDFTranslate onRef={(el) => scrollTo(el)} />
        <AiAssistant onRef={(el) => scrollTo(el)} ref={AiAssistantRef} />
        <UserReviews />
        <LastBlock />
      </div>
    </Layout>
  )
})

const GetStartBtn = (p: { classes?: string; styles?: CSSProperties; mode?: 'text' | 'btn' }) => {
  const { t } = useTranslationWithType()
  const type = p?.mode === 'text' ? 'text' : 'btn'

  return (
    <div onClick={doStart}>
      {type === 'btn' && (
        <div
          className={classNames(style['get-start-btn'], 'clickable', p?.classes)}
          style={p?.styles}
        >
          <span className={style['btn-text']}>{t('home.getStart')}</span>
          <AmeIcon className={style['btn-icon']} type='icon-line-global_arrow' />
        </div>
      )}
      {type === 'text' && (
        <div
          className={classNames(
            style['get-start-text-btn'],
            style['text'],
            'clickable',
            p?.classes
          )}
          style={p?.styles}
        >
          <span className={style['btn-text']}>{t('home.getStart')}</span>
          <AmeIcon className={style['btn-icon']} type='icon-line-global_arrow' />
        </div>
      )}
    </div>
  )
}

const Top = () => {
  const { t } = useTranslationWithType()
  const BASE_CLS = 'top'
  const cls = (cls: string) => {
    return `${BASE_CLS}_${cls}`
  }
  return (
    <div className={classNames(style['block'], style['top'])}>
      <div className={style[cls('left')]}>
        <div className={style[cls('left-title')]}>
          {t('slogen')}
          <br />
          {t('home.top.title1')}
          <br />
          {t('home.top.title2')}
        </div>
        <div className={style[cls('left-desc')]}>{t('home.top.desc')}</div>
        <div className={classNames('clickable', style[cls('left-btn')])} onClick={doStart}>
          <span>{t('home.getStartedForFree')}</span>
          <img src={commonUtils.getStaticFile('home/btn-star.png')} alt='' />
        </div>
        <div className={style[cls('left-like-usr')]}>
          <img
            className={style[cls('left-like-usr-item')]}
            src={commonUtils.getStaticFile('home/avatar/avatar1.png')}
            alt=''
          />
          <img
            className={style[cls('left-like-usr-item')]}
            src={commonUtils.getStaticFile('home/avatar/avatar2.png')}
            alt=''
          />
          <span className={style[cls('left-like-usr-cnt')]}>100K+peoples like</span>
        </div>
      </div>
      <div className={style[cls('right')]}>
        <img
          className={classNames(style.bg1)}
          src={commonUtils.getStaticFile('home/banner-top-1.png')}
        />
        <img
          className={classNames(style.bg2)}
          src={commonUtils.getStaticFile('home/banner-top-2.png')}
        />
        <div className={classNames(style.videoBox)}>
          <video
            className=''
            src='https://cdn.noam.cool/static/video-home.mp4'
            muted
            autoPlay
            loop
            webkit-playsinline='true'
            playsInline
            x5-video-player-type='h5'
          ></video>
        </div>
      </div>
    </div>
  )
}

const HowItWork = () => {
  const { t } = useTranslationWithType()
  const BASE_CLS = 'how-it-work'
  const cls = (cls: string) => {
    return `${BASE_CLS}_${cls}`
  }

  return (
    <div className={classNames(style['block'], style[BASE_CLS])}>
      <div className={style[cls('title')]}>{t('home.howItWork.title')}</div>
      <div className={style[cls('desc')]}>{t('home.howItWork.desc')}</div>
      <div className={style[cls('content')]}>
        <div className={style[cls('content-info')]}>
          <img
            className={style[cls('content-info-pic')]}
            src={commonUtils.getStaticFile('home/hiw-1-1.png')}
          />
          <div className={style[cls('content-info-tag')]}>PDF Translation</div>
        </div>
        <div className={style[cls('content-info')]}>
          <img
            className={style[cls('content-info-pic')]}
            src={commonUtils.getStaticFile('home/hiw-2-1.png')}
          />
          <div className={style[cls('content-info-tag')]}>Video Translation & Summary</div>
        </div>
        <div className={style[cls('content-info')]}>
          <img
            className={style[cls('content-info-pic')]}
            src={commonUtils.getStaticFile('home/hiw-3-1.png')}
          />
          <div className={style[cls('content-info-tag')]}>Web Translation & Summary</div>
        </div>
        <div className={style[cls('content-info')]}>
          <img
            className={style[cls('content-info-pic')]}
            src={commonUtils.getStaticFile('home/hiw-4-1.png')}
          />
          <div className={style[cls('content-info-tag')]}>Image Translation</div>
        </div>

        <img
          className={classNames(style.howItWorkBg)}
          src={commonUtils.getStaticFile('home/bg__how-it-work.png')}
        />
      </div>
    </div>
  )
}

const PDFTranslate = observer(({ onRef }: { onRef: (el: HTMLDivElement) => void }) => {
  const BASE_CLS = 'pdf-translate'
  const cls = (cls: string) => {
    return `${BASE_CLS}_${cls}`
  }

  const { t } = useTranslationWithType()
  const ref = useRef<HTMLDivElement>(null)
  const to = commonUtils.getUrlParams().to
  useEffect(() => {
    if (to === 'pdf' && ref.current) {
      onRef(ref.current)
    }
  }, [])

  return (
    <div className={classNames(style['block'], style[BASE_CLS])} ref={ref} id='pdfDiv'>
      <div className={style[cls('title')]}>{t('home.PDFTranslate.title')}</div>
      <div className={style[cls('desc')]}>{t('home.PDFTranslate.desc')}</div>
      <div className={style[cls('content')]}>
        <div className={style[cls('content-left')]}>
          <img
            className={classNames(style.bg)}
            src={commonUtils.getStaticFile('pdf/bg__pdf-video-v1.png')}
          />
          <div className={classNames(style.videoBox)}>
            <video
              className=''
              src='https://cdn.noam.cool/static/video-PDF.mp4'
              muted
              autoPlay
              loop
              webkit-playsinline='true'
              playsInline
              x5-video-player-type='h5'
            ></video>
          </div>
        </div>
        <div className={style[cls('content-right')]}>
          <div className={style[cls('content-right-item')]}>
            <img
              className={style[cls('content-right-item-icon')]}
              src={commonUtils.getStaticFile('home/pdf-translate-icon-1.png')}
              alt=''
            />
            <div className={style[cls('content-right-item-intro')]}>
              <p>{t('home.PDFTranslate.sub1.title')}</p>
              <p>{t('home.PDFTranslate.sub1.desc')}</p>
            </div>
          </div>
          <div className={style[cls('content-right-item')]}>
            <img
              className={style[cls('content-right-item-icon')]}
              src={commonUtils.getStaticFile('home/pdf-translate-icon-2.png')}
              alt=''
            />
            <div className={style[cls('content-right-item-intro')]}>
              <p>{t('home.PDFTranslate.sub2.title')}</p>
              <p>{t('home.PDFTranslate.sub2.desc')}</p>
            </div>
          </div>
          <div className={style[cls('content-right-item')]}>
            <img
              className={style[cls('content-right-item-icon')]}
              src={commonUtils.getStaticFile('home/pdf-translate-icon-3.png')}
              alt=''
            />
            <div className={style[cls('content-right-item-intro')]}>
              <p>{t('home.PDFTranslate.sub3.title')}</p>
              <p>{t('home.PDFTranslate.sub3.desc')}</p>
            </div>
          </div>
          <GetStartBtn mode='btn' styles={{ marginTop: 56, marginLeft: 56 }} />
        </div>
      </div>
    </div>
  )
})

const tabs = [
  {
    key: 'video',
    name: 'home.aiAssistant.panel4.tab1.key',
    src: 'https://cdn.noam.cool/static/video-v.mp4',
    title: 'home.aiAssistant.panel4.tab1.title',
    desc: 'home.aiAssistant.panel4.tab1.desc',
  },
  {
    key: 'image',
    name: 'home.aiAssistant.panel4.tab2.key',
    src: commonUtils.getStaticFile('home/video-img-1.mp4'),
    title: 'home.aiAssistant.panel4.tab2.title',
    desc: 'home.aiAssistant.panel4.tab2.desc',
  },
  {
    key: 'web',
    name: 'home.aiAssistant.panel4.tab3.key',
    src: commonUtils.getStaticFile('home/video-web-1.mp4'),
    title: 'home.aiAssistant.panel4.tab3.title',
    desc: 'home.aiAssistant.panel4.tab3.desc',
  },
]
const AiAssistant = forwardRef(({ onRef }: { onRef: (el: HTMLDivElement) => void }, ref) => {
  const { t } = useTranslationWithType()
  const BASE_CLS = 'ai-assistant'
  const cls = (cls: string) => {
    return `${BASE_CLS}_${cls}`
  }

  const [curTab, setCurTab] = useState<{
    key: string
    title: string
    desc: string
    src: string
    name: string
  }>(tabs[0])

  useImperativeHandle(
    ref,
    () => {
      return {
        setTab(to: string) {
          setCurTab(tabs.find((t) => t.key === to)!)
        },
      }
    },
    []
  )

  const divRef = useRef<HTMLDivElement>(null)
  const to = commonUtils.getUrlParams().to
  useEffect(() => {
    if (divRef.current && (to === 'web' || to === 'image' || to === 'video')) {
      setCurTab(tabs.find((t) => t.key === to)!)
      onRef(divRef.current!)
    }
  }, [])

  return (
    <div className={classNames(style['block'], style[BASE_CLS])}>
      <div className={style[cls('title')]}>{t('home.aiAssistant.title')}</div>
      <div className={style[cls('desc')]}>{t('home.aiAssistant.desc')}</div>
      <div className={style[cls('content')]}>
        <div className={style[cls('content-panel-1')]}>
          <div className={style[cls('content-panel-1-box')]}>
            <img
              className={style[cls('content-panel-1-box-icon')]}
              src={commonUtils.getStaticFile('home/ai-assistant-icon-1-1.png')}
            />
            <div className={style[cls('content-panel-1-box-title')]}>
              {t('home.aiAssistant.panel1.title')}
            </div>
            <div className={style[cls('content-panel-1-box-desc')]}>
              {t('home.aiAssistant.panel1.desc')}
            </div>
            <GetStartBtn styles={{ marginTop: 56 }} />
          </div>
          <img
            className={style[cls('content-panel-1-pic')]}
            src={commonUtils.getStaticFile('home/ai-assistant-pic-1-1.png')}
            alt=''
          />
        </div>
        <div className={style[cls('content-panel-2')]}>
          <div className={style[cls('content-panel-2-left')]}>
            <div
              className={style[cls('content-panel-2-left-box')]}
              style={{ position: 'relative' }}
            >
              <img
                className={style[cls('content-panel-2-left-box-icon')]}
                src={commonUtils.getStaticFile('home/ai-assistant-icon-2.png')}
              />
              <div className={style[cls('content-panel-2-left-box-title')]}>
                {t('home.aiAssistant.panel2.left.title')}
              </div>
              <div className={style[cls('content-panel-2-left-box-desc')]}>
                {t('home.aiAssistant.panel2.left.desc')}
              </div>
              <GetStartBtn mode='text' styles={{ position: 'absolute', right: 60, top: 20 }} />
            </div>
            <img
              className={style[cls('content-panel-2-left-pic')]}
              src={commonUtils.getStaticFile('home/ai-assistant-pic-2-1.png')}
              alt=''
            />
          </div>
          <div className={style[cls('content-panel-2-right')]}>
            <div
              className={style[cls('content-panel-2-right-box')]}
              style={{ position: 'relative' }}
            >
              <img
                className={style[cls('content-panel-2-left-box-icon')]}
                src={commonUtils.getStaticFile('home/ai-assistant-icon-3.png')}
              />
              <div className={style[cls('content-panel-2-right-box-title')]}>
                {t('home.aiAssistant.panel2.right.title')}
              </div>
              <div className={style[cls('content-panel-2-right-box-desc')]}>
                {t('home.aiAssistant.panel2.right.desc')}
              </div>
              <GetStartBtn mode='text' styles={{ position: 'absolute', right: 60, top: 20 }} />
            </div>
            <img
              className={style[cls('content-panel-2-right-pic')]}
              src={commonUtils.getStaticFile('home/ai-assistant-pic-3-1.png')}
              alt=''
            />
          </div>
        </div>
        <div className={style[cls('content-panel-3')]}>
          <div className={style[cls('content-panel-3-item')]}>
            <img
              className={style[cls('content-panel-3-item-icon')]}
              src={commonUtils.getStaticFile('home/ai-assistant-icon-4.png')}
              alt=''
            />
            <div>
              <p className={style[cls('content-panel-3-item-title')]}>
                {t('home.aiAssistant.panel3.one.title')}
              </p>
              <p className={style[cls('content-panel-3-item-desc')]}>
                {t('home.aiAssistant.panel3.one.desc')}
              </p>
            </div>
          </div>
          <div className={style[cls('content-panel-3-item')]}>
            <img
              className={style[cls('content-panel-3-item-icon')]}
              src={commonUtils.getStaticFile('home/ai-assistant-icon-5.png')}
              alt=''
            />
            <div>
              <p className={style[cls('content-panel-3-item-title')]}>
                {t('home.aiAssistant.panel3.two.title')}
              </p>
              <p className={style[cls('content-panel-3-item-desc')]}>
                {t('home.aiAssistant.panel3.two.desc')}
              </p>
            </div>
          </div>
          <div className={style[cls('content-panel-3-item')]}>
            <img
              className={style[cls('content-panel-3-item-icon')]}
              src={commonUtils.getStaticFile('home/ai-assistant-icon-6.png')}
              alt=''
            />
            <div>
              <p className={style[cls('content-panel-3-item-title')]}>
                {t('home.aiAssistant.panel3.three.title')}
              </p>
              <p className={style[cls('content-panel-3-item-desc')]}>
                {t('home.aiAssistant.panel3.three.desc')}
              </p>
            </div>
          </div>
        </div>
        <div className={style[cls('content-panel-4')]} ref={divRef} id='translateDiv'>
          <div className={style[cls('content-panel-4-left')]}>
            <img src={commonUtils.getStaticFile('pdf/bg__ai-assistant-video.png')} alt='' />

            {tabs.map((t) => {
              return (
                <div
                  key={t.key}
                  className={classNames(style.videoBox, {
                    [style.web]: curTab.key === 'web',
                    [style.active]: t.key === curTab.key,
                  })}
                >
                  <video
                    className=''
                    src={curTab.src}
                    muted
                    autoPlay
                    loop
                    webkit-playsinline='true'
                    playsInline
                    x5-video-player-type='h5'
                  ></video>
                </div>
              )
            })}
          </div>
          <div className={style[cls('content-panel-4-right')]}>
            <div className={style[cls('content-panel-4-right-tab')]}>
              {tabs.map((tab, index) => {
                return (
                  <div
                    className={classNames(
                      style[cls('content-panel-4-right-tab-item')],
                      'clickable',
                      {
                        [style['active']]: curTab.key === tab.key,
                      }
                    )}
                    key={index}
                    onClick={() => setCurTab(tab)}
                  >
                    {t(tab.name as any)}
                  </div>
                )
              })}
            </div>
            <div className={style[cls('content-panel-4-right-content')]}>
              <p>{t(curTab.title as any)}</p>
              <p>{t(curTab.desc as any)}</p>
              <div className={style[cls('content-panel-4-right-content-tags')]}>
                <div className={style[cls('content-panel-4-right-content-tags-item')]}>
                  <img src={commonUtils.getStaticFile('home/ai-assistant-tab-tag-1.png')} alt='' />
                  <span>{t('home.aiAssistant.panel4.tag1')}</span>
                </div>
                <div className={style[cls('content-panel-4-right-content-tags-item')]}>
                  <img src={commonUtils.getStaticFile('home/ai-assistant-tab-tag-2.png')} alt='' />
                  <span>{t('home.aiAssistant.panel4.tag2')}</span>
                </div>
                <div className={style[cls('content-panel-4-right-content-tags-item')]}>
                  <img src={commonUtils.getStaticFile('home/ai-assistant-tab-tag-3.png')} alt='' />
                  <span>{t('home.aiAssistant.panel4.tag3')}</span>
                </div>
              </div>
              <GetStartBtn />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
})

const UserReviews = () => {
  const { t } = useTranslationWithType()
  const BASE_CLS = 'user-reviews'
  const cls = (cls: string) => {
    return `${BASE_CLS}_${cls}`
  }
  const data: Array<{ name: string; avatar: string; content: string }> = [
    {
      name: 'Michelle',
      avatar: commonUtils.getStaticFile('home/avatar/avatar1.png'),
      content:
        'I have been searching for a simple and accurate translation software, and Noam completely meets my needs. I love its clean interface, which allows me to quickly translate article content, and it provides multiple language options, making my work much easier.',
    },
    {
      name: 'Stephanie',
      avatar: commonUtils.getStaticFile('home/avatar/avatar2.png'),
      content:
        'This is the best AI translation plugin for my browser. I have downloaded about 30 similar components and tried almost all of them. I found it to be very fast and accurate, translating almost in real time, which is very convenient for me as someone who often needs to communicate with foreign friends.',
    },
    {
      name: 'Jamie',
      avatar: commonUtils.getStaticFile('home/avatar/avatar3.png'),
      content:
        'I especially like its multilingual support. Whether I need to translate text from English to French, Spanish, or any other language, this software can handle it perfectly. Moreover, it not only supports simple phrase translation but also can process long articles or PDF files, making it an essential application extension.',
    },
    {
      name: 'Rosalie',
      avatar: commonUtils.getStaticFile('home/avatar/avatar4.png'),
      content: `The first time I used this plugin, I fell in love with its interface and interaction. I didn't need to spend too much time learning how to use it. Just select the text to be translated, click 'Translate,' and it will immediately provide accurate translation results. This is incredibly convenient for busy people like me.`,
    },
    {
      name: 'Mitty.P',
      avatar: commonUtils.getStaticFile('home/avatar/avatar5.png'),
      content: `I have already recommended this amazing AI extension tool to all my friends and colleagues around me. Noam has given me so many surprises. Not only can it accurately translate content I don't understand while browsing the web, but it can also condense a large amount of content into simple and understandable summaries.`,
    },
    {
      name: 'Ellie.Barrett',
      avatar: commonUtils.getStaticFile('home/avatar/avatar6.png'),
      content: `As a researcher, I deal with a large amount of foreign language literature and materials every day. After trying many translation software, I found that Noam translation is not only accurate but also easy to use, it is simply my lifesaver!`,
    },
  ]
  return (
    <div className={classNames(style[BASE_CLS])}>
      <div className={style[cls('header-box')]}>
        <p className={style[cls('title')]}>{t('home.userReviews.title')}</p>
        <p className={style[cls('desc')]}>{t('home.userReviews.desc')}</p>
      </div>
      <img
        className={style[cls('divider')]}
        src={commonUtils.getStaticFile('home/user-reviews-divider.png')}
        alt=''
      />
      <div className={style[cls('card')]}>
        <div className={classNames(style.bgBox)}>
          <img
            className={style[cls('card-bg')]}
            src={commonUtils.getStaticFile('home/user-reviews-bg.png')}
            alt=''
          />
        </div>
        <div className={classNames(style['block'], style[cls('card-box')])}>
          {data.map((d) => {
            return (
              <div key={d.name} className={style[cls('card-box-item')]}>
                <div className={classNames('flex-row')}>
                  <img className={classNames(style.avatar)} src={d.avatar} />
                  <span className={classNames(style.name)}>{d.name}</span>
                </div>
                <div className={classNames(style.starBox, 'flex-row', 'align-left')}>
                  <AmeIcon type='icon-fill-global_star' className={style.ameIcon}></AmeIcon>
                  <AmeIcon type='icon-fill-global_star' className={style.ameIcon}></AmeIcon>
                  <AmeIcon type='icon-fill-global_star' className={style.ameIcon}></AmeIcon>
                  <AmeIcon type='icon-fill-global_star' className={style.ameIcon}></AmeIcon>
                  <AmeIcon type='icon-fill-global_star' className={style.ameIcon}></AmeIcon>
                </div>
                <div className={classNames(style.content)}>{d.content}</div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

const LastBlock = () => {
  const { t } = useTranslationWithType()
  const BASE_CLS = 'last-block'
  const cls = (cls: string) => {
    return `${BASE_CLS}_${cls}`
  }
  return (
    <div className={classNames(style[BASE_CLS])}>
      <div className={classNames(style['block'], style[cls('content')])}>
        <img
          className={style[cls('bg')]}
          src={commonUtils.getStaticFile('home/last-block-bg-1.png')}
          alt=''
        />
        <p className={style[cls('title')]}>{t('slogen')}</p>
        <p className={style[cls('desc')]}>{t('home.lastBlock.desc')}</p>
        <div className={classNames('clickable', style[cls('btn')])} onClick={doStart}>
          <span>{t('home.getStartedForFree')}</span>
          <img src={commonUtils.getStaticFile('home/btn-star.png')} alt='' />
        </div>
      </div>
    </div>
  )
}

export default HomePage
